import React from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';

import { useCommunityContext } from 'components/CommunityContext';
import { Space } from 'interfaces/space';
import { isAdmin } from 'utils/userHelpers';
import { LockSimple } from '@thinkific/chalk/react-icons';
import { TabNav } from '@thinkific/chalk';
import { atomic } from '@thinkific/chalk/atomic';
import CommunityUsersAvatars from 'components/CommunityUsers/components/CommunityUsersAvatars';

interface SpaceInfoProps {
  space: Space;
}

// eslint-disable-next-line no-shadow
export enum SpaceInfoTabs {
  INDEX = 'index',
  MEMBERS = 'members',
  EDIT = 'edit',
}

export const spaceInfoContainer = atomic({
  backgroundColor: 'onLight.surface.primary',
  color: 'onLight.text.primary',
  borderRadius: 'sm',
  fontSize: 'display.base',
  boxShadow: 'onLight.shadow.sm',
  paddingBottom: 'space.600',
  position: 'relative',
  marginBottom: 'space.400',
});

export const centeredContainer = atomic({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'start',
  gap: 'space.100',
});

export const spaceDetailsContainer = atomic({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'space.100',
  justifyContent: 'space-between',
  padding: 'space.200',
  alignItems: 'center',
});

export const spaceNameContainer = atomic({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'space.100',
});

export const tabContainer = atomic({
  position: 'absolute',
  bottom: 0,
  width: 'full',
});

export const truncateName = atomic({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const SpaceInfo = ({ space }: SpaceInfoProps) => {
  const { t } = useTranslation();
  const { community, currentUser } = useCommunityContext();
  const router = useRouter();
  const { spaceId } = router.query;
  const isPrivate = space.isExclusive;

  const tabs = [
    {
      id: SpaceInfoTabs.INDEX,
      label: t('components-spaceInfo-posts-tab', 'Feed'),
      href: `/communities/${community.id}/spaces/${spaceId}`,
    },
    {
      id: SpaceInfoTabs.MEMBERS,
      label: t('components-spaceInfo-members-tab', 'Members'),
      href: `/communities/${community.id}/spaces/${spaceId}/members`,
    },
    ...(isAdmin(currentUser)
      ? [
          {
            id: SpaceInfoTabs.EDIT,
            label: t('components-spaceInfo-edit-tab', 'Settings'),
            href: `/communities/${community.id}/spaces/${spaceId}/edit`,
          },
        ]
      : []),
  ];

  const totalUsersCount = space.spaceMembers?.totalCount || 0;

  return (
    <div className={spaceInfoContainer}>
      <div className={spaceDetailsContainer}>
        <span className={spaceNameContainer}>
          <span className={truncateName}>{space.name}</span>
          {isPrivate && <LockSimple />}
        </span>
        <CommunityUsersAvatars
          tooltipTitle={t(
            'components-spaceInfo-total_member_count_title',
            '{{spaceName}} has {{userCount}} members',
            { spaceName: space.name, userCount: totalUsersCount }
          )}
          totalUsers={totalUsersCount}
          users={space.spaceMembers?.edges
            ?.map(({ node }) => node.user)
            ?.splice(0, 5)}
        />
      </div>
      <div className={tabContainer}>
        <TabNav
          currentTabId={
            tabs.find((tab) => router.route.includes(tab.id))?.id || 'index'
          }
          tabs={tabs}
        />
      </div>
    </div>
  );
};

export default SpaceInfo;
